import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';

import { css } from '@emotion/react';
import find from 'lodash/find';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';

import Button from '@components/Common/Button';
import DatePicker from '@components/Common/DatePicker';
import Form from '@components/Common/Form';
import FormRow from '@components/Common/FormRow';
import FormSection from '@components/Common/FormSection';
import Input from '@components/Common/Input';
import LoadingSpinner from '@components/Common/LoadingSpinner';
import SelectBox from '@components/Common/SelectBox';
import ConfirmModal from '@components/Modals/ConfirmModal';

import { GroupNewFormInputs } from '@type/group';

import { useGroupDetail, useGroupUpdate } from '@hooks/api/group/useGroupAPIs';
import { useInternalRouter } from '@hooks/useInternalRouter';

import { titleStyle } from '@styles/common';
import { palette } from '@styles/theme';

import { GROUP_STATUS_NO_COLOR, GROUP_TYPES } from '@constants/commons';
import {
  MAX_AMOUNT,
  bizNoValidateOptions,
  dateValidateOptions,
  newAmountValidateOptions,
} from '@constants/validation';

import { bizNumberFormat, comma } from '@utils/utils';

/** 조합 관리 > 상세 > 수정하기 */
const GroupEdit = () => {
  const router = useInternalRouter();
  const { groupId } = useParams();
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  /** 조합 상세 API */
  const { data } = useGroupDetail();

  /** 조합 수정 API */
  const { mutate, isLoading } = useGroupUpdate(setConfirmModalOpen);

  /** react-hook-form Init */
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<GroupNewFormInputs>({
    defaultValues: {
      name: data?.name,
      type: find(GROUP_TYPES, { value: data?.type }),
      bizNo: bizNumberFormat(data?.bizNo),
      beginAt: data?.beginAt,
      endsAt: data?.endsAt,
      totalPayment: comma(data?.totalPayment),
      contractAmount: comma(data?.contractAmount),
      status: find(GROUP_STATUS_NO_COLOR, { value: data?.status }),
      primaryInvestments:
        data?.primaryInvestments?.length > 0
          ? data.primaryInvestments
          : [{ priority: 1, criterion: '' }],
      requiredInvestment: data?.requiredInvestment,
      actualInvestmentFund: comma(data?.actualInvestmentFund),
    },
  });

  /** 주목적투자기준 동적 필드 */
  const {
    fields: primaryInvestmentFields,
    append: appendPrimaryInvestment,
    remove: removePrimaryInvestment,
  } = useFieldArray({
    control,
    name: 'primaryInvestments',
  });

  /** 금액 콤마 */
  const handleOnChangeNumber = (
    e: ChangeEvent<HTMLInputElement>,
    setValue: Dispatch<SetStateAction<string>>,
    fieldOnChange: any
  ) => {
    /** 숫자만 입력값으로 넘김 */
    const validInputValue = e.target.value
      .replaceAll(',', '')
      .replace(/[^0-9]/g, '')
      .replace(/^0+/, '');

    setValue(validInputValue === '' ? '' : comma(validInputValue));
    fieldOnChange(validInputValue === '' ? '' : comma(validInputValue));
  };

  /** 사업자번호 Input onChange Handler */
  const handleBizNoChange = (e: any, setValue: any, onChange: any) => {
    setValue(bizNumberFormat(e.target.value));
    onChange(bizNumberFormat(e.target.value));
  };

  const onConfirm: SubmitHandler<GroupNewFormInputs> = async () => {
    try {
      setConfirmModalOpen(true);
    } catch (error: any) {
      console.log('ERROR_GROUP_EDIT_CONFIRM');
    }
  };

  const onSubmit: SubmitHandler<GroupNewFormInputs> = async ({
    name,
    type,
    bizNo,
    beginAt,
    endsAt,
    totalPayment,
    contractAmount,
    status,
    primaryInvestments,
    requiredInvestment,
    actualInvestmentFund,
  }) => {
    try {
      mutate({
        name,
        type: type?.value,
        bizNo: bizNo?.replaceAll('-', ''),
        beginAt,
        endsAt,
        totalPayment: totalPayment?.replaceAll(',', ''),
        contractAmount: contractAmount?.replaceAll(',', ''),
        status: status?.value,
        primaryInvestments: primaryInvestments.map((item, index) => ({
          priority: index + 1,
          criterion: item.criterion,
        })),
        requiredInvestment: requiredInvestment,
        actualInvestmentFund: actualInvestmentFund?.replaceAll(',', ''),
      });
    } catch (error: any) {
      console.log('ERROR_GROUP_EDIT_SUBMIT');
    }
  };

  return (
    <>
      <div css={[titleStyle, { justifyContent: 'space-between' }]}>
        조합 수정
        <div css={buttonWrap}>
          <Button
            label="목록으로"
            theme="grayscale"
            handleClick={() => router.push(`/group/${groupId}`)}
          />
          <Button
            label="수정하기"
            theme="secondary"
            handleClick={handleSubmit(onConfirm)}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onConfirm)}>
        <Form>
          <FormRow>
            <FormSection label="조합명" required>
              <Input
                name="name"
                width={590}
                control={control}
                errors={errors}
                rules={{
                  required: '조합명을 입력해 주세요.',
                  minLength: {
                    value: 2,
                    message: '최소 2자이상 입력해 주세요.',
                  },
                  maxLength: {
                    value: 50,
                    message: '최대 50자까지 입력할 수 있습니다.',
                  },
                }}
                placeHolder="조합명을 입력해 주세요."
              />
            </FormSection>
            <FormSection label="조합구분" required>
              <SelectBox
                width={300}
                options={GROUP_TYPES}
                name="type"
                control={control}
                errors={errors}
                rules={{ required: '조합구분값을 선택해 주세요.' }}
                placeholder="조합구분값을 선택해 주세요."
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection
              label={
                <>
                  고유번호
                  <br />
                  (사업자번호)
                </>
              }
              required
            >
              <Input
                name="bizNo"
                control={control}
                width={590}
                errors={errors}
                rules={bizNoValidateOptions}
                placeHolder="숫자만 입력해 주세요."
                handleChange={handleBizNoChange}
              />
            </FormSection>
            <FormSection label={'조합상태'} required>
              <SelectBox
                width={300}
                options={GROUP_STATUS_NO_COLOR}
                name="status"
                control={control}
                errors={errors}
                rules={{ required: '조합상태를 선택해 주세요.' }}
                placeholder={'조합상태를 선택해 주세요.'}
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="약정총액" required>
              <Input
                name="contractAmount"
                control={control}
                errors={errors}
                width={590}
                rules={newAmountValidateOptions(
                  '0',
                  MAX_AMOUNT,
                  '약정총액을 입력해 주세요.'
                )}
                placeHolder="숫자만 입력해 주세요."
                handleChange={handleOnChangeNumber}
              />
            </FormSection>
            <FormSection label="납입총액" required>
              <Input
                name="totalPayment"
                control={control}
                errors={errors}
                width={590}
                rules={newAmountValidateOptions(
                  '0',
                  MAX_AMOUNT,
                  '납입총액을 입력해 주세요.'
                )}
                placeHolder="숫자만 입력해 주세요."
                handleChange={handleOnChangeNumber}
              />
            </FormSection>
          </FormRow>

          <FormRow>
            <FormSection label="실투자재원" required>
              <Input
                name="actualInvestmentFund"
                control={control}
                errors={errors}
                width={590}
                rules={newAmountValidateOptions(
                  '0',
                  MAX_AMOUNT,
                  '실투자재원을 입력해 주세요.'
                )}
                placeHolder="숫자만 입력해 주세요."
                handleChange={handleOnChangeNumber}
              />
            </FormSection>
            <FormSection label="운용기간" required>
              <div css={dateWrap}>
                <DatePicker
                  name="beginAt"
                  control={control}
                  errors={errors}
                  rules={dateValidateOptions}
                />
                <span>~</span>
                <DatePicker
                  name="endsAt"
                  control={control}
                  errors={errors}
                  rules={dateValidateOptions}
                />
              </div>
            </FormSection>
          </FormRow>

          <FormRow>
            <FormSection label="의무투자기준" required>
              <Input
                name="requiredInvestment"
                control={control}
                errors={errors}
                width={590}
                rules={{ required: '의무투자기준을 입력해 주세요.' }}
                placeHolder="의무투자기준을 입력해 주세요."
              />
            </FormSection>
            <FormSection label="주목적투자기준" required>
              <div css={primaryInvestmentsWrap}>
                {primaryInvestmentFields.map((field, index) => (
                  <div key={field.id} css={primaryInvestmentRow}>
                    <Input
                      name={`primaryInvestments.${index}.criterion`}
                      control={control}
                      errors={errors}
                      width={385}
                      rules={{ required: '주목적투자기준을 입력해 주세요.' }}
                      placeHolder="주목적투자기준을 입력해 주세요."
                    />
                    {primaryInvestmentFields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePrimaryInvestment(index)}
                        css={removeButton}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    appendPrimaryInvestment({
                      priority: primaryInvestmentFields.length + 1,
                      criterion: '',
                    })
                  }
                  css={addButton}
                >
                  + 추가
                </button>
              </div>
            </FormSection>
          </FormRow>
        </Form>
      </form>

      {confirmModalOpen && (
        <ConfirmModal
          title="수정하시겠습니까?"
          buttonText={['취소하기', '수정하기']}
          open={confirmModalOpen}
          handleOpen={() => setConfirmModalOpen(false)}
          handleCancel={() => setConfirmModalOpen(false)}
          handleConfirm={handleSubmit(onSubmit)}
        />
      )}

      {isLoading && <LoadingSpinner />}
    </>
  );
};

export default GroupEdit;

const buttonWrap = css`
  display: flex;
  gap: 10px;
`;

const dateWrap = css`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const primaryInvestmentsWrap = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 6px 0;
`;

const primaryInvestmentRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const removeButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;

  border-radius: 10px;
  background: ${palette.white};
  color: ${palette.gray5};
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: ${palette.gray1};
  }
`;

const addButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  margin-top: 6px;
  border: 1px dashed ${palette.gray3};
  border-radius: 4px;
  background: ${palette.white};
  color: ${palette.gray5};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  &:hover {
    background: ${palette.gray1};
    border-color: ${palette.gray4};
  }
`;
